import React, { useMemo } from 'react';
import { Rocket, Beaker, Eye, PauseCircle, Star } from 'lucide-react';
import { isQuickWin, effortTierForBadge } from '../../../config/opportunityBadge';

const formatRoiValue = (v: number): string =>
  v < 100 ? v.toFixed(1) : (v / 1_000_000).toFixed(1);

interface Opportunity {
  id: string;
  rank: number;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  roiEstimate: { low: number; high: number; unit?: string };
}

interface OIRSectionExecutionRoadmapProps {
  rankedOpportunities: Opportunity[];
  selectedOpportunities: Opportunity[];
}

type Bucket = 'launch' | 'pilot' | 'monitor' | 'defer';

// Launch-now is the Quick Win quadrant inside the top band; tiering below it uses the
// ROOT-derived effort tier rather than the stored string.
const getBucket = (opp: Opportunity): Bucket => {
  const tier = effortTierForBadge({ id: opp.id, implementationEffort: opp.implementationEffort });
  if (opp.rank <= 15 && isQuickWin({ id: opp.id, implementationEffort: opp.implementationEffort })) return 'launch';
  if (opp.rank <= 25 && tier !== 'High') return 'pilot';
  if (opp.rank <= 50) return 'monitor';
  return 'defer';
};

const BUCKET_CONFIG: Record<Bucket, {
  label: string;
  sub: string;
  icon: React.FC<{ className?: string }>;
  bg: string;
  border: string;
  headerBg: string;
}> = {
  launch: {
    label: 'Launch Now',
    sub: 'High-confidence, low-effort',
    icon: Rocket,
    bg: 'bg-emerald-50/60',
    border: 'border-emerald-200',
    headerBg: 'bg-emerald-600',
  },
  pilot: {
    label: 'Pilot',
    sub: 'Near-term, moderate complexity',
    icon: Beaker,
    bg: 'bg-[#18365E]/5',
    border: 'border-[#18365E]/20',
    headerBg: 'bg-[#18365E]',
  },
  monitor: {
    label: 'Monitor',
    sub: 'Revisit after constraints ease',
    icon: Eye,
    bg: 'bg-amber-50/40',
    border: 'border-amber-100',
    headerBg: 'bg-amber-500',
  },
  defer: {
    label: 'Defer',
    sub: 'Lower priority / high complexity',
    icon: PauseCircle,
    bg: 'bg-[#F5F7FA]',
    border: 'border-[#e5e7eb]',
    headerBg: 'bg-[#717182]',
  },
};

export const OIRSectionExecutionRoadmap: React.FC<OIRSectionExecutionRoadmapProps> = ({
  rankedOpportunities,
  selectedOpportunities,
}) => {
  const selectedIds = useMemo(() => new Set(selectedOpportunities.map((o) => o.id)), [selectedOpportunities]);

  const buckets = useMemo(() => {
    const result: Record<Bucket, Opportunity[]> = {
      launch: [],
      pilot: [],
      monitor: [],
      defer: [],
    };

    rankedOpportunities.forEach((opp) => {
      const bucket = getBucket(opp);
      if (result[bucket].length < 6) result[bucket].push(opp);
    });

    return result;
  }, [rankedOpportunities]);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Execution Roadmap</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          A board-ready sequencing view — what to launch now vs. stage, monitor, or defer
        </p>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The roadmap buckets initiatives into four execution postures based on rank and implementation effort. Selected
        initiatives are highlighted to keep the plan aligned to your current shortlist.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {(Object.keys(BUCKET_CONFIG) as Bucket[]).map((key) => {
          const config = BUCKET_CONFIG[key];
          const opps = buckets[key];
          const Icon = config.icon;

          return (
            <div key={key} className={`rounded-xl border ${config.border} ${config.bg} overflow-hidden`}>
              <div className={`${config.headerBg} px-3 py-2.5 flex items-center gap-2`}>
                <Icon className="w-3.5 h-3.5 text-white" />
                <div>
                  <p className="text-[11px] font-bold text-white">{config.label}</p>
                  <p className="text-[9px] text-white/70 leading-tight">{config.sub}</p>
                </div>
                <span className="ml-auto text-[10px] font-bold text-white bg-white/20 rounded-full px-2 py-0.5">
                  {opps.length}
                </span>
              </div>

              <div className="p-2.5 flex flex-col gap-1.5">
                {opps.length === 0 && (
                  <p className="text-[10px] text-[#717182] text-center py-2">None in this category</p>
                )}
                {opps.map((opp) => {
                  const isSelected = selectedIds.has(opp.id);
                  return (
                    <div
                      key={opp.id}
                      className={`rounded-lg border px-2.5 py-2 bg-white ${
                        isSelected ? 'border-[#FF6E4C]/40' : 'border-[#e5e7eb]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[10px] font-semibold text-[#18365E] leading-snug">
                          {opp.title}
                        </p>
                        {isSelected && (
                          <Star className="w-3 h-3 text-[#FF6E4C] fill-[#FF6E4C] shrink-0 mt-0.5" />
                        )}
                      </div>
                      <p className="text-[10px] font-bold text-[#FF6E4C]">
                        ${formatRoiValue(opp.roiEstimate.low)}–${formatRoiValue(opp.roiEstimate.high)}M
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OIRSectionExecutionRoadmap;
